import { alpha } from '@mui/material';

const FileViewerStyles = {
  mainContainer: (theme) => ({
    p: 3,
    borderRadius: '16px',
    bgcolor: 'background.paper',
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    overflow: 'hidden'
  }),

  tabsContainer: (theme) => ({
    display: 'flex',
    gap: 1.5,
    mb: 2,
    p: 2,
    bgcolor: alpha(theme.palette.grey[50], 0.8),
    backdropFilter: 'blur(10px)',
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  }),

  tab: (theme, isActive, config) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2,
    py: 1.5,
    minWidth: 160,
    cursor: 'pointer',
    borderRadius: 2,
    border: `2px solid ${isActive ? config.color : 'transparent'}`,
    bgcolor: isActive ? config.bgColor : 'transparent',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${alpha(config.color, 0.03)} 0%, ${alpha(config.color, 0.08)} 100%)`,
      opacity: isActive ? 1 : 0,
      transition: 'opacity 0.2s ease'
    },
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 12px ${alpha(config.color, 0.15)}`,
      borderColor: config.color,
      '&:before': {
        opacity: 1
      }
    }
  }),

  tabIcon: (theme, isActive, config) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: 2,
    bgcolor: config.color,
    color: 'white',
    position: 'relative',
    zIndex: 1,
    boxShadow: isActive 
      ? `0 3px 8px ${alpha(config.color, 0.3)}` 
      : `0 2px 4px ${alpha(config.color, 0.2)}`,
    transition: 'box-shadow 0.2s ease'
  }),

  tabContent: {
    flex: 1,
    minWidth: 0,
    position: 'relative',
    zIndex: 1
  },

  tabTitle: (isActive, config) => ({
    fontWeight: isActive ? 700 : 600,
    color: isActive ? config.color : 'text.primary',
    fontSize: '0.875rem',
    lineHeight: 1.2,
    mb: 0.5,
    transition: 'color 0.2s ease'
  }),

  tabChip: (config) => ({
    height: 18,
    fontSize: '0.7rem',
    bgcolor: alpha(config.color, 0.1),
    color: config.color,
    border: `1px solid ${alpha(config.color, 0.2)}`,
    '& .MuiChip-label': { px: 1 }
  }),

  activeIndicator: (config) => ({
    width: 6,
    height: 6,
    borderRadius: '50%',
    bgcolor: config.color,
    boxShadow: `0 0 6px ${config.color}`,
    '@keyframes pulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.6 }
    },
    animation: 'pulse 2s ease-in-out infinite'
  }),

  // Toolbar
  toolbar: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    p: 2,
    bgcolor: alpha(theme.palette.grey[50], 0.5),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
  }),

  toolbarTitle: {
    fontWeight: 600,
    color: 'text.primary',
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },

  toolbarIcon: (theme, currentFile) => {
    const config = {
      'original': { color: '#1976d2' },
      'signed': { color: '#2e7d32' },
      'attachment': { color: '#7b1fa2' }
    };
    const fileConfig = config[currentFile?.type] || config.attachment;
    
    return {
      width: 24,
      height: 24,
      borderRadius: 1,
      bgcolor: fileConfig.color,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    };
  },

  fileCounter: {
    fontSize: '0.75rem',
    height: 24
  },

  navButton: (theme) => ({
    color: 'primary.main',
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      transform: 'scale(1.05)'
    },
    '&:disabled': {
      opacity: 0.3
    }
  }),

  controlButton: (theme) => ({
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      transform: 'scale(1.05)'
    }
  }),

  downloadButton: (theme) => ({
    color: 'success.main',
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: alpha(theme.palette.success.main, 0.08),
      transform: 'scale(1.05)'
    }
  }),

  // Viewer area
  viewerContainer: {
    height: '60vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    background: 'linear-gradient(145deg, #f0f2f5 0%, #e8eaf0 100%)',
    borderRadius: '12px',
    overflow: 'auto',
    p: 3,
    position: 'relative',
    border: '1px solid rgba(0, 0, 0, 0.08)'
  },

  fileViewerFrame: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2
  },

  imageStyle: (zoomLevel, rotation) => ({
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
    transition: 'transform 0.3s ease',
    borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
  }),

  // Loading state
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    py: 8
  },

  loadingSpinner: {
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' }
    },
    width: 48,
    height: 48,
    border: '4px solid rgba(25, 118, 210, 0.2)',
    borderTop: '4px solid #1976d2',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },

  loadingText: {
    color: '#1976d2',
    fontWeight: 500
  },

  noFileContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    py: 8,
    color: 'text.secondary'
  },

  noFileIcon: {
    fontSize: 64,
    color: 'rgba(0, 0, 0, 0.3)',
    mb: 2
  },

  noFileTitle: {
    fontWeight: 500,
    color: 'rgba(0, 0, 0, 0.6)'
  },

  navigationDots: (theme) => ({
    position: 'absolute',
    bottom: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    p: 1,
    borderRadius: 2,
    bgcolor: alpha(theme.palette.background.paper, 0.95),
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    border: '1px solid rgba(255,255,255,0.2)',
    transition: 'opacity 0.3s ease'
  }),

  navigationDot: {
    cursor: 'pointer',
    transition: 'transform 0.2s ease'
  },

  attachmentNotViewable: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '300px',
      p: 4,
      textAlign: 'center'
    },

    iconContainer: (theme) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 80,
      height: 80,
      borderRadius: '50%',
      backgroundColor: theme.palette.secondary.light,
      mb: 3
    }),

    title: {
      mb: 2
    },

    alert: {
      mb: 3,
      maxWidth: 400
    },

    metaContainer: {
      display: 'flex',
      gap: 2,
      alignItems: 'center',
      mb: 3,
      flexWrap: 'wrap',
      justifyContent: 'center'
    },

    downloadButton: {
      px: 4,
      py: 1.5,
      borderRadius: 3,
      textTransform: 'none',
      fontSize: '1.1rem',
      fontWeight: 'bold',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
      }
    },

    description: {
      mt: 2,
      maxWidth: 400
    }
  },

  pdfError: {
    container: {
      textAlign: 'center',
      p: 4
    },

    icon: {
      fontSize: 48,
      color: 'warning.main',
      mb: 2
    },

    title: {
      color: 'warning.main'
    },

    description: {
      mt: 1,
      mb: 3
    }
  },

  container: {
    p: 3,
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
  },

  header: (theme) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 3,
    pb: 2,
    borderBottom: `2px solid ${theme.palette.divider}`,
    position: 'relative'
  }),

  fileName: {
    fontWeight: 600,
    fontSize: '1.1rem'
  },

  attachmentIcon: {
    display: 'flex',
    alignItems: 'center',
    p: 0.5,
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.05)'
  },

  fileTypeChip: {
    fontWeight: 600,
    fontSize: '0.85rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    py: 0.7,
    px: 1.5,
    minWidth: 120,
    '& .MuiChip-label': {
      px: 1
    }
  },

  fileSizeChip: {
    fontSize: '0.75rem',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)'
    }
  },

  controlsContainer: {
    display: 'flex',
    gap: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    p: 1,
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(10px)'
  },

  filesList: {
    mt: 3,
    p: 2,
    backgroundColor: 'rgba(248, 250, 252, 0.8)',
    borderRadius: '12px',
    border: '1px solid rgba(0, 0, 0, 0.08)'
  },

  filesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 2
  },

  fileCard: {
    p: 2,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    borderRadius: '12px',
    border: '2px solid transparent',
    backgroundColor: 'white',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
      borderColor: 'rgba(25, 118, 210, 0.3)'
    }
  },

  fileCardActive: {
    borderColor: '#1976d2',
    backgroundColor: 'rgba(25, 118, 210, 0.05)',
    boxShadow: '0 8px 32px rgba(25, 118, 210, 0.2)',
    transform: 'translateY(-1px)'
  },

  fileCardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    position: 'relative'
  },

  fileCardIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: '12px',
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    flexShrink: 0
  },

  fileCardInfo: {
    flex: 1,
    minWidth: 0
  },

  fileCardName: {
    fontWeight: 600,
    fontSize: '0.9rem',
    mb: 1,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.3
  },

  fileCardMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexWrap: 'wrap'
  },

  fileCardBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    '& .MuiBadge-dot': {
      width: 12,
      height: 12,
      borderRadius: '50%',
      border: '2px solid white',
      boxShadow: '0 2px 8px rgba(25, 118, 210, 0.4)'
    }
  }
};

export default FileViewerStyles;